import { TFunction } from 'i18next';
import { Conversation, ConversationParticipantRef, Message } from './types/Conversation';
import { getConversationKind, isAiAssistantDirectConversation, isExternalConversation, isWhatsAppGroupConversation } from './utils/conversationClassification';
import { inferContactChannelTypeFromMessageSource } from './utils/conversationListRealtime';
import { maskChannelIdentitiesInText } from '../contacts/contactChannelUtils';

export type ConversationChannelIconKind =
  | 'internal'
  | 'employee'
  | 'ai'
  | 'lead'
  | 'customer'
  | 'phone'
  | 'whatsApp'
  | 'instagram'
  | 'messenger'
  | 'telegram'
  | 'signal'
  | 'webchat'
  | 'tiktok'
  | 'other';

export interface ConversationChannelIcon {
  key: string;
  kind: ConversationChannelIconKind;
  label: string;
}

const SOCIAL_MEDIA_CHANNEL_ICON_KINDS = new Set<ConversationChannelIconKind>([
  'whatsApp',
  'telegram',
  'instagram',
  'messenger',
  'webchat',
]);

export const isWebchatParticipant = (participant: Pick<ConversationParticipantRef, 'contactChannelType' | 'contactChannelLabel' | 'contactChannelValue'>): boolean => {
  if (participant.contactChannelLabel?.trim().toLowerCase() === 'webchat') {
    return true;
  }

  return participant.contactChannelValue?.trim().toLowerCase().startsWith('webchat:') ?? false;
};

export const participantMatchesChannelFilter = (
  participant: ConversationParticipantRef,
  filterChannel: 'whatsApp' | 'telegram' | 'instagram' | 'messenger' | 'webchat',
): boolean => {
  if (filterChannel === 'webchat') {
    return isWebchatParticipant(participant);
  }

  return participant.contactChannelType === filterChannel;
};

export const isSocialMediaChannelIcon = (icon: ConversationChannelIcon): boolean =>
  SOCIAL_MEDIA_CHANNEL_ICON_KINDS.has(icon.kind);

export type ConversationChannelMasking = {
  maskChannelValues?: boolean;
};

const maybeMaskChannelText = (value: string, masking?: ConversationChannelMasking): string => {
  return masking?.maskChannelValues ? maskChannelIdentitiesInText(value) : value;
};

const getParticipantName = (participant: ConversationParticipantRef, t: TFunction<'conversations'>, masking?: ConversationChannelMasking): string => {
  const trimmedName = participant.name?.trim();
  if (trimmedName) {
    return maybeMaskChannelText(trimmedName, masking);
  }

  switch (participant.type) {
    case 'companyMember':
      return t('employee', { ns: 'conversations' });
    case 'customer':
      return t('customer', { ns: 'conversations' });
    case 'lead':
      return t('lead', { ns: 'conversations' });
    case 'aiAssistant':
      return t('aiAssistant', { ns: 'conversations' });
    default:
      return t('participant', { ns: 'conversations' });
  }
};

const getChannelTypeLabel = (participant: ConversationParticipantRef, t: TFunction<'conversations'>): string => {
  if (isWebchatParticipant(participant)) {
    return t('webchatChannel', { ns: 'conversations' });
  }

  switch (participant.contactChannelType) {
    case 'phone':
      return t('phoneChannel', { ns: 'conversations' });
    case 'whatsApp':
      return t('whatsAppChannel', { ns: 'conversations' });
    case 'instagram':
      return t('instagramChannel', { ns: 'conversations' });
    case 'telegram':
      return t('telegramChannel', { ns: 'conversations' });
    case 'messenger':
      return t('messengerChannel', { ns: 'conversations' });
    case 'signal':
      return t('signalChannel', { ns: 'conversations' });
    default:
      return t('otherChannel', { ns: 'conversations' });
  }
};

export const getParticipantChannelSummary = (
  participant: ConversationParticipantRef,
  t: TFunction<'conversations'>,
  masking?: ConversationChannelMasking,
): string | null => {
  if (!participant.contactChannelType || !participant.contactChannelValue?.trim()) {
    return null;
  }

  const channelTypeLabel = getChannelTypeLabel(participant, t);
  const channelLabel = participant.contactChannelLabel?.trim();
  const prefix = channelLabel ? `${channelTypeLabel} (${channelLabel})` : channelTypeLabel;
  return `${prefix}: ${maybeMaskChannelText(participant.contactChannelValue.trim(), masking)}`;
};

const pushUnique = (
  icons: ConversationChannelIcon[],
  seenKeys: Set<string>,
  icon: ConversationChannelIcon,
) => {
  if (seenKeys.has(icon.key)) {
    return;
  }

  seenKeys.add(icon.key);
  icons.push(icon);
};

export const getParticipantChannelIcon = (participant: ConversationParticipantRef, t: TFunction<'conversations'>): ConversationChannelIcon | null => {
  if (isWebchatParticipant(participant)) {
    return { key: 'webchat', kind: 'webchat', label: t('webchatChannel', { ns: 'conversations' }) };
  }

  switch (participant.contactChannelType) {
    case 'phone':
      return { key: 'phone', kind: 'phone', label: t('phoneChannel', { ns: 'conversations' }) };
    case 'whatsApp':
      return { key: 'whatsApp', kind: 'whatsApp', label: t('whatsAppChannel', { ns: 'conversations' }) };
    case 'instagram':
      return { key: 'instagram', kind: 'instagram', label: t('instagramChannel', { ns: 'conversations' }) };
    case 'telegram':
      return { key: 'telegram', kind: 'telegram', label: t('telegramChannel', { ns: 'conversations' }) };
    case 'messenger':
      return { key: 'messenger', kind: 'messenger', label: t('messengerChannel', { ns: 'conversations' }) };
    case 'signal':
      return { key: 'signal', kind: 'signal', label: t('signalChannel', { ns: 'conversations' }) };
    case 'other':
      if (participant.contactChannelLabel?.trim().toLowerCase() === 'instagram') {
        return { key: 'instagram', kind: 'instagram', label: t('instagramChannel', { ns: 'conversations' }) };
      }

      return { key: 'other', kind: 'other', label: t('otherChannel', { ns: 'conversations' }) };
    default:
      return null;
  }
};

export const getParticipantConversationIcons = (
  participant: ConversationParticipantRef,
  t: TFunction<'conversations'>,
): ConversationChannelIcon[] => {
  const icons: ConversationChannelIcon[] = [];
  const seenKeys = new Set<string>();

  if (participant.type === 'companyMember') {
    pushUnique(icons, seenKeys, {
      key: 'employee',
      kind: 'employee',
      label: t('employeeParticipants', { ns: 'conversations' }),
    });
  } else if (participant.type === 'aiAssistant') {
    pushUnique(icons, seenKeys, {
      key: 'ai',
      kind: 'ai',
      label: t('aiAssistantIncluded', { ns: 'conversations' }),
    });
  } else if (participant.type === 'lead') {
    pushUnique(icons, seenKeys, {
      key: 'lead',
      kind: 'lead',
      label: t('leadParticipant', { ns: 'conversations' }),
    });
  } else if (participant.type === 'customer') {
    pushUnique(icons, seenKeys, {
      key: 'customer',
      kind: 'customer',
      label: t('customerParticipant', { ns: 'conversations' }),
    });
  }

  const channelIcon = getParticipantChannelIcon(participant, t);
  if (channelIcon) {
    pushUnique(icons, seenKeys, channelIcon);
  }

  return icons;
};

export const getParticipantConversationIconTitles = (
  participant: ConversationParticipantRef,
  t: TFunction<'conversations'>,
  masking?: ConversationChannelMasking,
): Map<string, string> => {
  const participantName = getParticipantName(participant, t, masking);
  const participantSummary = getParticipantChannelSummary(participant, t, masking);
  const icons = getParticipantConversationIcons(participant, t);

  return new Map(
    icons.map(icon => {
      const channelIcon = getParticipantChannelIcon(participant, t);
      if (channelIcon?.key === icon.key && participantSummary) {
        return [icon.key, `${icon.label}\n${participantName}: ${participantSummary}`];
      }

      return [icon.key, `${icon.label}\n${participantName}`];
    }),
  );
};

export const getConversationChannelIconTitles = (
  conversation: Conversation,
  t: TFunction<'conversations'>,
  masking?: ConversationChannelMasking,
): Map<string, string> => {
  const detailsByKey = new Map<string, string[]>();
  const appendDetail = (key: string, detail: string) => {
    const current = detailsByKey.get(key);
    if (current) {
      current.push(detail);
      return;
    }

    detailsByKey.set(key, [detail]);
  };

  conversation.participants.forEach(participant => {
    const participantName = getParticipantName(participant, t, masking);

    if (participant.type === 'companyMember') {
      appendDetail('employee', participantName);
    } else if (participant.type === 'aiAssistant') {
      appendDetail('ai', participantName);
    } else if (participant.type === 'customer') {
      appendDetail('customer', participantName);
    } else if (participant.type === 'lead') {
      appendDetail('lead', participantName);
    }

    const summary = getParticipantChannelSummary(participant, t, masking);
    const channelIcon = getParticipantChannelIcon(participant, t);
    if (!summary || !channelIcon) {
      return;
    }

    appendDetail(channelIcon.key, `${participantName}: ${summary}`);
  });

  const icons = getConversationChannelIcons(conversation, t);
  return new Map(
    icons.map(icon => {
      const details = detailsByKey.get(icon.key);
      const uniqueDetails = details
        ? Array.from(new Set(details.map(detail => detail.trim()).filter(Boolean)))
        : [];
      const title = uniqueDetails.length > 0
        ? `${icon.label}\n${uniqueDetails.join('\n')}`
        : icon.label;
      return [icon.key, title];
    }),
  );
};

export const getConversationChannelIcons = (conversation: Conversation, t: TFunction<'conversations'>): ConversationChannelIcon[] => {
  const icons: ConversationChannelIcon[] = [];
  const seenKeys = new Set<string>();
  const hasExternalParticipant = isExternalConversation(conversation);

  if (!hasExternalParticipant) {
    pushUnique(icons, seenKeys, {
      key: 'internal',
      kind: 'internal',
      label: t('internalConversation', { ns: 'conversations' }),
    });
  }

  if (conversation.containsWhatsAppMessages || isWhatsAppGroupConversation(conversation)) {
    pushUnique(icons, seenKeys, {
      key: 'whatsApp',
      kind: 'whatsApp',
      label: t('whatsAppChannel', { ns: 'conversations' }),
    });
  }

  if (conversation.containsInstagramMessages || conversation.containsInstagramComments) {
    pushUnique(icons, seenKeys, {
      key: 'instagram',
      kind: 'instagram',
      label: t('instagramChannel', { ns: 'conversations' }),
    });
  }

  const latestMessage = conversation.messages.reduce<Message | null>((latest, message) => {
    if (!latest) {
      return message;
    }

    return new Date(message.timestamp).getTime() >= new Date(latest.timestamp).getTime()
      ? message
      : latest;
  }, null);

  const latestMessageChannelType = inferContactChannelTypeFromMessageSource(latestMessage?.source);
  if (conversation.containsMessengerComments || latestMessageChannelType === 'messenger') {
    pushUnique(icons, seenKeys, {
      key: 'messenger',
      kind: 'messenger',
      label: t('messengerChannel', { ns: 'conversations' }),
    });
  }

  if (latestMessageChannelType === 'telegram') {
    pushUnique(icons, seenKeys, {
      key: 'telegram',
      kind: 'telegram',
      label: t('telegramChannel', { ns: 'conversations' }),
    });
  } else if (latestMessageChannelType === 'tikTok') {
    pushUnique(icons, seenKeys, {
      key: 'tiktok',
      kind: 'tiktok',
      label: t('tiktokChannel', { ns: 'conversations' }),
    });
  } else if (
    latestMessageChannelType === 'other'
    && latestMessage?.source.trim().toLowerCase() === 'webchat'
  ) {
    pushUnique(icons, seenKeys, {
      key: 'webchat',
      kind: 'webchat',
      label: t('webchatChannel', { ns: 'conversations' }),
    });
  }

  conversation.participants.forEach(participant => {
    if (participant.type === 'companyMember') {
      pushUnique(icons, seenKeys, {
        key: 'employee',
        kind: 'employee',
        label: t('employeeParticipants', { ns: 'conversations' }),
      });
      return;
    }

    if (participant.type === 'aiAssistant') {
      pushUnique(icons, seenKeys, {
        key: 'ai',
        kind: 'ai',
        label: t('aiAssistantIncluded', { ns: 'conversations' }),
      });
      return;
    }

    if (participant.type === 'lead') {
      pushUnique(icons, seenKeys, {
        key: 'lead',
        kind: 'lead',
        label: t('leadParticipant', { ns: 'conversations' }),
      });

      const channelIcon = getParticipantChannelIcon(participant, t);
      if (channelIcon) {
        pushUnique(icons, seenKeys, channelIcon);
      }
      return;
    }

    if (participant.type === 'customer') {
      pushUnique(icons, seenKeys, {
        key: 'customer',
        kind: 'customer',
        label: t('customerParticipant', { ns: 'conversations' }),
      });

      const channelIcon = getParticipantChannelIcon(participant, t);
      if (channelIcon) {
        pushUnique(icons, seenKeys, channelIcon);
      }
    }
  });

  return icons;
};

export const getConversationRowChannelIcons = (conversation: Conversation, t: TFunction<'conversations'>): ConversationChannelIcon[] =>
  getConversationChannelIcons(conversation, t).filter(isSocialMediaChannelIcon);

export const getChannelBadgeInlineLabel = (kind: ConversationChannelIconKind): string | undefined => {
  switch (kind) {
    case 'whatsApp':
      return 'whatsapp';
    case 'telegram':
      return 'telegram';
    case 'instagram':
      return 'instagram';
    case 'messenger':
      return 'Messenger';
    case 'webchat':
      return 'webchat';
    case 'tiktok':
      return 'tiktok';
    default:
      return undefined;
  }
};

export const getConversationHeaderChannelIcons = (conversation: Conversation, t: TFunction<'conversations'>): ConversationChannelIcon[] => {
  const socialIcons = getConversationRowChannelIcons(conversation, t);
  if (socialIcons.length > 0) {
    return [socialIcons[0]];
  }

  const conversationKind = getConversationKind(conversation);

  if (conversationKind === 'whatsapp-group' || isWhatsAppGroupConversation(conversation)) {
    return [{
      key: 'whatsApp-group',
      kind: 'whatsApp',
      label: t('whatsAppGroup', { ns: 'conversations', defaultValue: 'WhatsApp group' }),
    }];
  }

  if (conversationKind === 'internal-channel') {
    return [{
      key: 'internal',
      kind: 'internal',
      label: t('channel', { ns: 'conversations' }),
    }];
  }

  if (conversationKind === 'internal-direct') {
    if (isAiAssistantDirectConversation(conversation)) {
      return [{
        key: 'ai',
        kind: 'ai',
        label: t('aiAssistant', { ns: 'conversations' }),
      }];
    }

    return [{
      key: 'employee',
      kind: 'employee',
      label: t('directMessagesHeader', { ns: 'conversations' }),
    }];
  }

  const externalParticipant = conversation.participants.find(participant => participant.type === 'customer' || participant.type === 'lead');
  const channelIcon = externalParticipant ? getParticipantChannelIcon(externalParticipant, t) : null;
  if (channelIcon) {
    return [channelIcon];
  }

  if (externalParticipant?.type === 'lead') {
    return [{
      key: 'lead',
      kind: 'lead',
      label: t('leadParticipant', { ns: 'conversations' }),
    }];
  }

  if (externalParticipant?.type === 'customer') {
    return [{
      key: 'customer',
      kind: 'customer',
      label: t('customerParticipant', { ns: 'conversations' }),
    }];
  }

  return [];
};

export const resolveExternalConversationAvatarIcon = (
  conversation: Conversation,
  t: TFunction<'conversations'>,
  participant?: ConversationParticipantRef | null,
): ConversationChannelIcon | null => {
  if (!isExternalConversation(conversation)) {
    return null;
  }

  if (participant) {
    const participantIcon = getParticipantChannelIcon(participant, t);
    if (participantIcon) {
      return participantIcon;
    }
  }

  const headerIcon = getConversationHeaderChannelIcons(conversation, t)[0];
  if (!headerIcon) {
    return null;
  }

  if (headerIcon.kind === 'internal' || headerIcon.kind === 'employee' || headerIcon.kind === 'ai') {
    return null;
  }

  return headerIcon;
};